import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IAdminAccount, IAdminAccountTeamRef, useAdminAccountsQuery } from '../../api/admin';
import { formatDate } from '../../utils/dates';
import AccountDialog from './AccountDialog';
import AccountStatusChip from './AccountStatusChip';
import AdminGridLink from './AdminGridLink';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	teamId?: number;
	organizationId?: number;
	height?: string | number;
}

export const money = (v: number | null) =>
	v === null ? '' : v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const KEY_DATE_LABEL: Record<NonNullable<IAdminAccount['key_date_kind']>, string> = {
	trial_ends: 'Trial ends',
	ends: 'Ends',
	ended: 'Ended',
};

const isOver = (a: IAdminAccount) => a.stage !== 'churned' && a.stage !== 'lost';

/** One row per account (commercial relationship). Edit via the pencil; creation lives in the page header. */
export default function AccountsTable({ teamId, organizationId, height = 'calc(100vh - 310px)' }: Props) {
	const { data: accounts, isLoading } = useAdminAccountsQuery({ teamId, organizationId });
	const [editing, setEditing] = useState<IAdminAccount | null>(null);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Account',
			flex: 1,
			minWidth: 150,
			maxWidth: 260,
			renderCell: (p: GridRenderCellParams) => (
				<AdminGridLink to={`/admin/accounts/${p.row.id}`} state={{ accountName: p.value }}>
					{p.value}
				</AdminGridLink>
			),
		},
		{
			field: 'organization_name',
			headerName: 'Organization',
			flex: 1,
			minWidth: 150,
			maxWidth: 240,
			renderCell: (p: GridRenderCellParams) => (
				<AdminGridLink to={`/admin/organizations/${p.row.organization_id}`} state={{ organizationName: p.value }}>
					{p.value}
				</AdminGridLink>
			),
		},
		{
			field: 'teams',
			headerName: 'Teams',
			flex: 1.2,
			minWidth: 160,
			sortable: false,
			valueGetter: ({ row }) => ((row.teams ?? []) as IAdminAccountTeamRef[]).map((t) => t.name).join(', '),
			renderCell: (p: GridRenderCellParams) => {
				const teams = (p.row.teams ?? []) as IAdminAccountTeamRef[];
				if (teams.length === 0) {
					return null;
				}
				return (
					<Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
						{teams.map((t) => (
							<Chip key={t.id} label={t.name} size="small" variant="outlined" />
						))}
					</Stack>
				);
			},
		},
		{
			field: 'status',
			headerName: 'Stage',
			width: 105,
			renderCell: (p: GridRenderCellParams) => <AccountStatusChip account={p.row as IAdminAccount} />,
		},
		{
			field: 'seats_active',
			headerName: 'Seats Active',
			width: 110,
			type: 'number',
			renderCell: (p: GridRenderCellParams) => {
				const a = p.row as IAdminAccount;
				if (!isOver(a)) {
					return null;
				}
				const over = a.seats !== null && a.seats_active > a.seats;
				return (
					<Tooltip title={over ? 'More active users than seats' : 'Active users on the covered teams / seats'}>
						<Typography variant="body2" color={over ? 'error' : undefined} fontWeight={over ? 600 : undefined}>
							{a.seats_active}
							{a.seats !== null ? ` / ${a.seats}` : ''}
						</Typography>
					</Tooltip>
				);
			},
		},
		{
			field: 'key_date',
			headerName: 'Key Date',
			width: 170,
			valueGetter: ({ row }) => (row as IAdminAccount).key_date ?? '',
			renderCell: (p: GridRenderCellParams) => {
				const a = p.row as IAdminAccount;
				if (!a.key_date_kind || !a.key_date) {
					return null;
				}
				return (
					<Typography variant="body2" color={a.key_date_kind === 'ended' ? 'error' : undefined}>
						{KEY_DATE_LABEL[a.key_date_kind]} {formatDate(a.key_date)}
					</Typography>
				);
			},
		},
		{
			field: 'value',
			headerName: 'Value',
			width: 110,
			type: 'number',
			valueFormatter: ({ value }) => money(value),
		},
		{
			field: 'owner_name',
			headerName: 'Owner',
			width: 150,
			valueGetter: ({ row }) => row.owner_name ?? row.owner_email ?? '',
		},
		{ field: 'notes', headerName: 'Notes', flex: 1.5, minWidth: 160 },
		{
			field: 'actions',
			headerName: '',
			width: 48,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (p: GridRenderCellParams) => (
				<Tooltip title="Edit account">
					<IconButton size="small" onClick={() => setEditing(p.row as IAdminAccount)}>
						<EditIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<Box sx={{ height, width: '100%' }}>
			<DataGrid
				rows={accounts ?? []}
				columns={columns}
				loading={isLoading}
				density="compact"
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
			<AccountDialog open={!!editing} onClose={() => setEditing(null)} account={editing} />
		</Box>
	);
}
